// Minimal helpers to allow unquoted values in JSON-like strings

// Applies `fn` only to the parts of `s` that sit OUTSIDE JSON string literals.
//
// The quoting helpers below are plain regex replaces and cannot see string boundaries, so a token
// written inside ordinary text ({ "note": "prefix: [PLACEHOLDER_X]" }) used to be rewritten with
// nested quotes and broke the very parse this file exists to rescue. Only a structural value
// position needs the leniency, and that is always outside a string.
const outsideStrings = (s, fn) => {
	let out = ''
	let segment = ''
	let inString = false
	let escaped = false

	for (const ch of s) {
		if (inString) {
			out += ch
			if (escaped) escaped = false
			else if (ch === '\\') escaped = true
			else if (ch === '"') inString = false
			continue
		}

		if (ch === '"') {
			out += fn(segment) + ch
			segment = ''
			inString = true
			continue
		}

		segment += ch
	}

	return out + fn(segment)
}

// Quote bracketed placeholder tokens (e.g., [PLACEHOLDER_FOO]) when used as values
// Only match uppercase letters/digits/underscores to avoid matching real arrays like : [1, 2].
// The optional ":INT" suffix is the typed placeholder form and names the same field, so it has to
// be accepted here too or a typed token written unquoted fails to parse.
const quoteBracketValues = s => outsideStrings(s, segment =>
	segment.replace(/(:\s*)(\[[A-Z0-9_]+(?::[Ii][Nn][Tt])?\])/g, (m, p1, token) => {
		const escaped = token.replace(/"/g, '\\"')
		return `${p1}"${escaped}"`
	}))

// Quote unquoted value tokens after a colon, capturing until the next delimiter (comma, closing brace/bracket, or newline)
// This ensures full URLs and similar tokens are quoted correctly
const quoteBarewordValues = s => outsideStrings(s, segment => segment.replace(/(:\s*)([^,}\]\r\n]+)/g, (m, p1, rawVal) => {
	const value = (rawVal || '').trim()
	// Nothing to quote. Reachable because the scan above hands over the text immediately before a
	// string literal, which is just the structural colon and whitespace.
	if (value === '') return m
	const lower = value.toLowerCase()
	// Keep booleans/null as-is
	if (lower === 'true' || lower === 'false' || lower === 'null') return m
	// Skip if already a string/object/array
	const first = value[0]
	if (first === '"' || first === '{' || first === '[') return m
	// Skip numbers (int/float/scientific)
	if (/^-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(value)) return m
	// Otherwise, wrap the full token in quotes
	const escaped = value.replace(/"/g, '\\"')
	return `${p1}"${escaped}"`
}))

export function relaxJsonString (input) {
	if (typeof input !== 'string') return input
	let out = input
	// Leave numbers, existing strings, objects, and arrays as-is; just quote obvious barewords
	out = quoteBracketValues(out)
	out = quoteBarewordValues(out)
	return out
}

export function parseLenientJson (input) {
	if (typeof input !== 'string') return input
	try {
		return JSON.parse(input)
	} catch (e) {
		const relaxed = relaxJsonString(input)
		return JSON.parse(relaxed)
	}
}

export function isLenientJsonValid (input) {
	if (input == null || (typeof input === 'string' && input.trim() === '')) return true
	try {
		parseLenientJson(input)
		return true
	} catch (e) {
		return false
	}
}
